/**
 * RelancesPanel — Auto-relance cockpit for stale immersion invites.
 *
 * Surfaces three buckets:
 *   • H+24  amber  → soft reminder needed (candidat hasn't viewed magic link)
 *   • H+48  red    → urgent — call them or send firm template
 *   • J-1   blue   → day-before reminder (candidat + référent both pinged)
 *
 * Each card:
 *   • Avatar + name + offer + lieu
 *   • "Envoyer la relance" → opens SendMessageModal with the right template
 *     pre-selected (builtin_relance_h24 / h48 / j1)
 *   • "Marquer comme relancé" → POST /api/relances/{id}/ack/{step}
 *   • "Ne plus relancer" toggle → PATCH /api/candidates/{id}/no-relance
 *
 * Visible to: admin, pré-recruteur (only their own candidates),
 * référent (only candidates with them as immersion_referent). Backend
 * auto-scopes; we just render what we get.
 */
import { motion } from "framer-motion";
import {
    AlertCircle,
    AlertTriangle,
    Bell,
    BellOff,
    Calendar,
    Check,
    Clock,
    MessageCircle,
    Phone,
    Sparkles,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { api } from "../../lib/api";
import SendMessageModal from "../messaging/SendMessageModal";


const STEP_META = {
    h24: {
        label: "À relancer (24h)",
        tone: "amber",
        chipBg: "bg-amber-50 border-amber-300 text-amber-800",
        Icon: Bell,
        templateId: "builtin_relance_h24",
        templateLabel: "Relance douce",
        defaultCategory: "relance_h24",
    },
    h48: {
        label: "Urgent (48h)",
        tone: "red",
        chipBg: "bg-red-50 border-red-300 text-red-700",
        Icon: AlertTriangle,
        templateId: "builtin_relance_h48",
        templateLabel: "Relance urgente",
        defaultCategory: "relance_h48",
    },
    j1: {
        label: "Rappel J-1",
        tone: "blue",
        chipBg: "bg-sky-50 border-sky-300 text-sky-700",
        Icon: Calendar,
        templateId: "builtin_relance_j1",
        templateLabel: "Rappel J-1",
        defaultCategory: "relance_j1",
    },
};


export default function RelancesPanel() {
    const [data, setData] = useState({ h24: [], h48: [], j1: [] });
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(null); // { candidate, step }
    const [acking, setAcking] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/relances");
            setData(data);
        } catch (e) {
            // silent — dashboard shouldn't break if the panel fails
            console.warn("relances load failed", e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const total = (data.h24?.length || 0) + (data.h48?.length || 0) + (data.j1?.length || 0);

    async function handleAck(candidateId, step) {
        setAcking(`${candidateId}-${step}`);
        try {
            await api.post(`/relances/${candidateId}/ack/${step}`);
            toast.success("Relance marquée comme traitée");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setAcking(null);
        }
    }

    async function handleNoRelance(candidateId) {
        try {
            await api.patch(`/candidates/${candidateId}/no-relance`, { no_relance: true });
            toast.success("Candidat retiré des relances automatiques");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    }

    function openMessageModal(candidate, step) {
        setSending({ candidate, step });
    }

    if (loading) {
        return (
            <div className="card-soft p-5 animate-pulse" data-testid="relances-panel-loading">
                <div className="h-5 w-44 bg-slate-200 rounded mb-3" />
                <div className="h-20 bg-slate-100 rounded-xl" />
            </div>
        );
    }

    if (total === 0) {
        // Subtle "all clear" — keep it small so it doesn't waste space.
        return (
            <div
                className="card-soft p-4 flex items-center gap-3"
                data-testid="relances-panel-empty"
            >
                <div className="h-9 w-9 rounded-full bg-emerald-50 text-emerald-600 inline-flex items-center justify-center shrink-0">
                    <Check className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-extrabold text-shariti-black">
                        Aucune relance en attente
                    </div>
                    <div className="text-xs font-medium text-slate-500">
                        Tous vos candidats invités ont confirmé ou sont à jour.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-soft p-5 md:p-6"
                data-testid="relances-panel"
            >
                <header className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-2xl bg-shariti-orange-soft text-shariti-orange inline-flex items-center justify-center">
                            <Sparkles className="h-5 w-5" />
                        </div>
                        <div>
                            <h3 className="text-lg font-extrabold tracking-tight text-shariti-black">
                                Candidats à relancer
                            </h3>
                            <p className="text-xs font-medium text-slate-500 -mt-0.5">
                                Auto-détection des immersions sans confirmation
                            </p>
                        </div>
                    </div>
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-shariti-orange text-white px-3 py-1 text-xs font-extrabold shadow-btn">
                        {total}
                    </div>
                </header>

                <div className="grid md:grid-cols-3 gap-3">
                    {["h24", "h48", "j1"].map((step) => (
                        <RelanceColumn
                            key={step}
                            step={step}
                            items={data[step] || []}
                            onAck={handleAck}
                            onNoRelance={handleNoRelance}
                            onSend={openMessageModal}
                            acking={acking}
                        />
                    ))}
                </div>
            </motion.div>

            {sending && (
                <SendMessageModal
                    candidate={sending.candidate}
                    defaultCategory={STEP_META[sending.step].defaultCategory}
                    onClose={() => setSending(null)}
                    onSent={async () => {
                        // Auto-ack on send so the card disappears
                        try {
                            await api.post(`/relances/${sending.candidate.id}/ack/${sending.step}`);
                        } catch (e) { /* silent */ }
                        setSending(null);
                        await load();
                    }}
                />
            )}
        </>
    );
}


function RelanceColumn({ step, items, onAck, onNoRelance, onSend, acking }) {
    const meta = STEP_META[step];
    const Icon = meta.Icon;
    return (
        <div
            className={`rounded-2xl border-2 ${meta.chipBg} p-3`}
            data-testid={`relance-col-${step}`}
        >
            <div className="flex items-center justify-between mb-2.5">
                <div className="inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wider">
                    <Icon className="h-3.5 w-3.5" />
                    {meta.label}
                </div>
                <span className="text-sm font-extrabold tabular-nums">
                    {items.length}
                </span>
            </div>

            {items.length === 0 ? (
                <div className="text-xs font-medium opacity-60 italic py-3 text-center">
                    Rien à signaler
                </div>
            ) : (
                <div className="space-y-2">
                    {items.map((c) => (
                        <RelanceCard
                            key={c.id}
                            candidate={c}
                            step={step}
                            onAck={() => onAck(c.id, step)}
                            onNoRelance={() => onNoRelance(c.id)}
                            onSend={() => onSend(c, step)}
                            isAcking={acking === `${c.id}-${step}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}


function RelanceCard({ candidate: c, step, onAck, onNoRelance, onSend, isAcking }) {
    const phoneClean = (c.phone || "").replace(/\s/g, "");
    const invitedAt = c.immersion_invited_at ? new Date(c.immersion_invited_at) : null;
    const scheduledAt = c.immersion_scheduled_at ? new Date(c.immersion_scheduled_at) : null;
    // Extract the Paris wall-clock components of the scheduled_at so
    // "Demain HHhMM" reflects the Paris hour, not the viewer's browser tz.
    const schedParis = scheduledAt
        ? new Intl.DateTimeFormat("fr-FR", {
            timeZone: "Europe/Paris", hour: "2-digit", minute: "2-digit", hour12: false,
        }).format(scheduledAt).replace(":", "h")
        : null;
    const subtitle = step === "j1" && schedParis
        ? `Demain ${schedParis}`
        : invitedAt
            ? `Invité ${invitedAt.toLocaleDateString("fr-FR", { timeZone: "Europe/Paris", day: "numeric", month: "short" })}`
            : "";

    return (
        <div
            className="rounded-xl bg-white/90 border border-white p-2.5 space-y-2"
            data-testid={`relance-card-${c.id}`}
        >
            <Link
                to={`/candidats/${c.id}`}
                className="flex items-start gap-2 hover:opacity-80 transition"
            >
                <div className="h-8 w-8 rounded-lg bg-shariti-orange-soft text-shariti-orange font-extrabold text-xs flex items-center justify-center shrink-0">
                    {c.first_name?.[0] || "?"}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-extrabold text-shariti-black break-words">
                        {c.first_name} {c.last_name}
                    </div>
                    <div className="text-[10px] font-semibold text-slate-500 break-words">
                        {subtitle}{c.immersion_location ? ` · ${c.immersion_location}` : ""}
                    </div>
                </div>
            </Link>

            <div className="flex flex-wrap gap-1.5">
                {phoneClean && (
                    <a
                        href={`tel:${phoneClean}`}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded-full bg-shariti-black text-white text-[10px] font-bold hover:bg-slate-800 transition"
                        data-testid={`relance-call-${c.id}`}
                    >
                        <Phone className="h-3 w-3" />
                        Appeler
                    </a>
                )}
                <button
                    type="button"
                    onClick={onSend}
                    data-testid={`relance-send-${c.id}-${step}`}
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-full bg-emerald-500 text-white text-[10px] font-bold hover:bg-emerald-600 transition"
                >
                    <MessageCircle className="h-3 w-3" />
                    Envoyer
                </button>
                <button
                    type="button"
                    onClick={onAck}
                    disabled={isAcking}
                    data-testid={`relance-ack-${c.id}-${step}`}
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-full bg-white border border-slate-300 text-slate-600 text-[10px] font-bold hover:bg-slate-50 transition disabled:opacity-50"
                    title="Marquer comme déjà relancé"
                >
                    <Check className="h-3 w-3" />
                    Traité
                </button>
                <button
                    type="button"
                    onClick={onNoRelance}
                    data-testid={`relance-mute-${c.id}`}
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-full bg-white border border-slate-300 text-slate-400 text-[10px] font-bold hover:text-slate-600 hover:bg-slate-50 transition"
                    title="Ne plus relancer ce candidat"
                >
                    <BellOff className="h-3 w-3" />
                </button>
            </div>
        </div>
    );
}
